import React, { useState, useRef } from 'react';
import { X, UploadCloud, FileVideo, Image as ImageIcon, Loader2, CheckCircle2 } from 'lucide-react';
import mediaService from '../../services/mediaService';

const MediaUploadModal = ({ isOpen, onClose, game, currentUser }) => {
    const [files, setFiles] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [progresses, setProgresses] = useState({});
    const [complete, setComplete] = useState(false);
    const fileInputRef = useRef(null);

    if (!isOpen) return null;

    const handleDragOver = (e) => {
        e.preventDefault();
    };

    const handleDrop = (e) => {
        e.preventDefault();
        handleFilesAdded(Array.from(e.dataTransfer.files));
    };

    const handleFileSelect = (e) => {
        handleFilesAdded(Array.from(e.target.files));
    };

    const handleFilesAdded = (newFiles) => {
        // Basic validation: limit to images/video
        const validFiles = newFiles.filter(file =>
            file.type.startsWith('image/') || file.type.startsWith('video/')
        );
        setFiles(prev => [...prev, ...validFiles]);
    };

    const removeFile = (indexToRemove) => {
        setFiles(files.filter((_, index) => index !== indexToRemove));
    };

    const handleUpload = async () => {
        if (files.length === 0) return;

        setUploading(true);
        setComplete(false);
        let allSuccess = true;

        for (let i = 0; i < files.length; i++) {
            const file = files[i];

            // Skip files that are already completed
            if (progresses[file.name] === 100) continue;

            let attempt = 0;
            const MAX_ATTEMPTS = 3;
            let success = false;

            while (attempt < MAX_ATTEMPTS && !success) {
                try {
                    await mediaService.uploadMediaForGame(
                        file,
                        game,
                        currentUser,
                        (percent) => {
                            setProgresses(prev => ({ ...prev, [file.name]: percent }));
                        }
                    );
                    success = true;
                } catch (err) {
                    attempt++;
                    console.error(`Attempt ${attempt} failed to upload ${file.name}`, err);
                    if (attempt >= MAX_ATTEMPTS) {
                        allSuccess = false;
                        // reset progress on total failure so user sees it didn't finish
                        setProgresses(prev => ({ ...prev, [file.name]: 0 }));
                    } else {
                        // wait a couple seconds before retrying
                        await new Promise(resolve => setTimeout(resolve, 2000));
                    }
                }
            }
        }

        setUploading(false);
        if (allSuccess) {
            setComplete(true);
            setTimeout(() => {
                onClose();
                setFiles([]);
                setComplete(false);
                setProgresses({});
            }, 2000);
        } else {
            alert("Some files failed to upload. Please try again.");
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]">
                <div className="px-6 py-4 border-b flex justify-between items-center bg-gray-50">
                    <h2 className="text-xl font-bold text-gray-800">Upload Match Media</h2>
                    <button onClick={onClose}
                        disabled={uploading}
                        className="p-2 hover:bg-gray-200 rounded-full transition-colors disabled:opacity-50">
                        <X size={20} className="text-gray-500" />
                    </button>
                </div>

                <div className="p-6 overflow-y-auto">
                    {complete ? (
                        <div className="flex flex-col items-center justify-center py-10 space-y-4">
                            <CheckCircle2 size={64} className="text-green-500" />
                            <p className="text-xl font-semibold text-gray-800">Upload Complete!</p>
                            <p className="text-gray-500 text-center">Your media has been safely stored in the team's Drive.</p>
                        </div>
                    ) : (
                        <>
                            {/* Dropzone */}
                            <div
                                className={`border-2 border-dashed rounded-xl p-8 mb-6 flex flex-col items-center justify-center text-center transition-colors cursor-pointer
                  ${uploading ? 'bg-gray-50 opacity-50 cursor-not-allowed' : 'hover:bg-blue-50 border-gray-300 hover:border-blue-400'}`}
                                onDragOver={handleDragOver}
                                onDrop={uploading ? undefined : handleDrop}
                                onClick={() => !uploading && fileInputRef.current?.click()}
                            >
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    onChange={handleFileSelect}
                                    multiple
                                    accept="image/*,video/*"
                                    className="hidden"
                                />
                                <UploadCloud size={48} className="text-blue-500 mb-4" />
                                <p className="text-lg font-semibold text-gray-700 mb-1">Click or drag files here</p>
                                <p className="text-sm text-gray-500">Supports photos and videos</p>
                            </div>

                            {/* File List */}
                            {files.length > 0 && (
                                <div className="space-y-3">
                                    <h3 className="font-semibold text-gray-700 mb-2">Selected Files ({files.length})</h3>
                                    {files.map((file, idx) => {
                                        const isVideo = file.type.startsWith('video/');
                                        const progress = progresses[file.name] || 0;

                                        return (
                                            <div key={idx} className="bg-gray-50 border border-gray-200 rounded-lg p-3 relative overflow-hidden">
                                                {/* Progress Bar Background */}
                                                {uploading && (
                                                    <div
                                                        className="absolute left-0 top-0 bottom-0 bg-blue-100/50 transition-all duration-300 ease-out z-0"
                                                        style={{ width: `${progress}%` }}
                                                    />
                                                )}

                                                <div className="relative z-10 flex items-center gap-3">
                                                    <div className={`p-2 rounded-lg ${isVideo ? 'bg-indigo-100 text-indigo-600' : 'bg-pink-100 text-pink-600'}`}>
                                                        {isVideo ? <FileVideo size={20} /> : <ImageIcon size={20} />}
                                                    </div>

                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm font-medium text-gray-800 truncate">{file.name}</p>
                                                        <p className="text-xs text-gray-500">
                                                            {(file.size / (1024 * 1024)).toFixed(2)} MB
                                                        </p>
                                                    </div>

                                                    {uploading && progresses[file.name] !== 100 ? (
                                                        <span className="text-sm font-semibold text-blue-600 min-w-[3rem] text-right">
                                                            {progress}%
                                                        </span>
                                                    ) : (
                                                        <div className="flex items-center">
                                                            {progress === 100 && (
                                                                <CheckCircle2 size={18} className="text-green-500 mr-2" />
                                                            )}
                                                            {!uploading && (
                                                                <button
                                                                    onClick={(e) => { e.stopPropagation(); removeFile(idx); }}
                                                                    className="p-1 hover:bg-gray-200 rounded text-gray-500 hover:text-red-500 transition-colors"
                                                                >
                                                                    <X size={16} />
                                                                </button>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </>
                    )}
                </div>

                {!complete && (
                    <div className="px-6 py-4 border-t bg-gray-50 flex justify-end gap-3">
                        <button
                            onClick={onClose}
                            disabled={uploading}
                            className="px-4 py-2 font-medium text-gray-600 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleUpload}
                            disabled={files.length === 0 || uploading}
                            className="px-6 py-2 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors disabled:bg-blue-300 flex items-center gap-2"
                        >
                            {uploading ? (
                                <>
                                    <Loader2 size={18} className="animate-spin" />
                                    Uploading...
                                </>
                            ) : (
                                `Upload ${files.length > 0 ? files.length : ''} Files`
                            )}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default MediaUploadModal;
